import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { NotificationResultDTO } from '@nora-monorepo/nora-api-client';
import { NotificationAction } from '@nora-monorepo/nora-api-client';
import type { BBox } from '@nora-monorepo/shared';
import {
  appLogger,
  useApiStore,
  useGenericUserStore,
  UserLoggingContext,
} from '@nora-monorepo/ui-shared';

export const useNotificationStore = defineStore('notificationStore', () => {
  const api = useApiStore();
  const userStore = useGenericUserStore();
  const notifications = ref<NotificationResultDTO[]>([]);

  /**
    Represents the number of unread geofence notifications. Will update automatically when API
   calls are made to mark notifications as read/unread or when new notifications are fetched.
   */
  const numUnreadGeofenceNotifications = computed(() => notifications.value.filter(notification => !notification.isRead && notification.action === NotificationAction.GEOFENCE_MODAL_OPEN).length);

  const LIMIT = 10;
  const nextCursor = ref<string | undefined>(undefined);
  const canLoadMoreNotifications = ref(true);
  const isLoading = ref(false);

  async function getMyNotifications(action?: NotificationAction, limit?: number, cursor?: string): Promise<NotificationResultDTO[]> {
    try {
      const result = await api.unwrap(
        api.userNotifications.getCurrentNotifications({
          action,
          limit: limit ?? LIMIT,
          cursor,
        }),
      );

      if (!result.data) {
        return [];
      }

      return result.data;
    }
    catch (err) {
      appLogger.error({
        message: 'Error fetching notifications',
        context: {
          user: new UserLoggingContext({ userId: userStore.user?.id ?? 'unknown', username: userStore.user?.username ?? 'unknown' }),
        },
        sensitiveContext: {
          error: {
            message: err instanceof Error ? err.message : 'Unknown error',
            name: err instanceof Error ? err.name : 'Unknown error',
            stack: err instanceof Error ? err.stack : 'No stack trace',
          },
        },
      });
    }

    return [];
  }

  async function loadInitialNotifications(action?: NotificationAction): Promise<void> {
    isLoading.value = true;
    nextCursor.value = undefined;
    canLoadMoreNotifications.value = true;

    const data = await getMyNotifications(action, LIMIT);

    if (data.length > 0) {
      nextCursor.value = data[data.length - 1].id;
    }

    if (data.length < LIMIT) {
      canLoadMoreNotifications.value = false;
    }

    notifications.value = data;
    isLoading.value = false;
  }

  async function loadMoreNotifications(action?: NotificationAction): Promise<void> {
    if (!canLoadMoreNotifications.value || isLoading.value) {
      return;
    }

    isLoading.value = true;
    const data = await getMyNotifications(action, LIMIT, nextCursor.value);

    if (data.length > 0) {
      nextCursor.value = data[data.length - 1].id;
    }

    if (data.length < LIMIT) {
      canLoadMoreNotifications.value = false;
    }

    for (const notification of data) {
      updateOrAddLocalNotification(notification);
    }
    isLoading.value = false;
  }

  async function fetchNotification(notificationId: string): Promise<NotificationResultDTO | null> {
    const notification = notifications.value?.find(
      notification => notification.id === notificationId,
    );

    if (!notification) {
      const newNotification = await api.unwrap(
        api.userNotifications.getNotification({ notificationId }),
      );

      if (newNotification.data) {
        notifications.value?.push(newNotification.data);

        return newNotification.data;
      }

      throw new Error('Notification not found');
    }

    return notification;
  }

  function setLocalNotificationReadStatus(
    notificationId: string,
    isRead: boolean,
  ): void {
    const notification = notifications.value?.find(
      notification => notification.id === notificationId,
    );

    if (notification) {
      notification.isRead = isRead;
    }
  }

  async function markNotificationAsRead(notificationId: string): Promise<boolean> {
    try {
      await api.userNotifications.markNotificationAsRead({ notificationId });
      setLocalNotificationReadStatus(notificationId, true);
    }
    catch (err) {
      setLocalNotificationReadStatus(notificationId, false);
      console.error('Error marking notification as read: ', err);
      return false;
    }

    return true;
  }

  async function markNotificationAsUnread(notificationId: string): Promise<boolean> {
    try {
      await api.userNotifications.markNotificationAsUnread({ notificationId });
      setLocalNotificationReadStatus(notificationId, false);
    }
    catch (err) {
      setLocalNotificationReadStatus(notificationId, true);
      console.error('Error marking notification as unread: ', err);
      return false;
    }

    return true;
  }

  async function deleteAllNotifications(): Promise<boolean> {
    try {
      await api.userNotifications.deleteAllNotifications();
    }
    catch (err) {
      console.error('Error marking notification as unread: ', err);
      return false;
    }
    notifications.value = [];
    return true;
  }

  async function deleteNotification(notificationId: string): Promise<boolean> {
    try {
      await api.userNotifications.deleteNotification({ notificationId });

      notifications.value = notifications.value.filter(
        notification => notification.id !== notificationId,
      );
    }
    catch (err) {
      console.error('Failed to delete notification', err);
      return false;
    }

    return true;
  }

  async function getNotificationsByBbox(
    bbox: BBox,
    operationId?: string,
  ): Promise<NotificationResultDTO[]> {
    try {
      const response = await api.unwrap(
        api.userNotifications.getNotificationsByBbox({
          minLongitude: bbox.minLongitude,
          minLatitude: bbox.minLatitude,
          maxLongitude: bbox.maxLongitude,
          maxLatitude: bbox.maxLatitude,
          operationId,
        }),
      );

      if (!response.data) {
        return [];
      }

      return response.data;
    }
    catch (err) {
      console.error('Error getting notifications within bbox', err);

      return [];
    }
  }

  function updateOrAddLocalNotification(notification: NotificationResultDTO): void {
    const index = notifications.value.findIndex(n => n.id === notification.id);

    if (index !== -1) {
      notifications.value[index] = notification;
    }
    else {
      notifications.value.push(notification);
    }
  }

  function resetData(): void {
    notifications.value = [];
  }

  async function bootstrap(): Promise<void> {
    await loadInitialNotifications();
  }

  return {
    notifications,
    canLoadMoreNotifications,
    isLoading,
    numUnreadGeofenceNotifications,
    deleteAllNotifications,
    deleteNotification,
    getMyNotifications,
    loadInitialNotifications,
    loadMoreNotifications,
    fetchNotification,
    markNotificationAsRead,
    markNotificationAsUnread,
    bootstrap,
    resetData,
    getNotificationsByBbox,
    updateOrAddLocalNotification,
  };
});
